import React, { useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { Alert, Button, Paper, TextField, Link } from '@mui/material';
import { useFormik } from 'formik';
import { useYupObject } from 'hooks';
import { gql, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Link as LinkRouter, useNavigate } from 'react-router-dom';

const RECOVERY_PASSWORD = gql`
  mutation ($email: String!, $bundle: String!) {
    requestNewPassword(email: $email, bundle: $bundle) {
      error
      value
    }
  }
`;

function RecoverPassword() {
  const yup = useYupObject();
  const navigate = useNavigate();
  const { t } = useTranslation(['_common', 'recover_password', 'api_errors']);
  const [error, setError] = useState<Boolean | String>(false);

  const [recoveryPassword, { error: errorApi, loading }] = useMutation(RECOVERY_PASSWORD, {
    onCompleted: (data) => {
      if (data?.requestNewPassword?.error) {
        return setError(data?.requestNewPassword?.value);
      }
      navigate('success', { replace: true });
    },
  });

  const validationSchema = yup.object({
    email: yup.string().required().trim().email(),
  });

  const initialValues = {
    email: '',
  };

  const formik = useFormik({
    validationSchema,
    initialValues,
    onSubmit: ({ email }) => {
      setError(false);
      recoveryPassword({
        variables: { email, bundle: 'br.com.segware.mysecurity' },
      });
    },
  });

  return (
    <Paper square elevation={3} component="form" onSubmit={formik.handleSubmit} sx={{ p: 5 }}>
      <TextField
        label={t('_common:email')}
        name="email"
        id="email"
        autoFocus
        fullWidth
        placeholder={t('_common:email')}
        helperText={formik.touched.email && formik.errors.email}
        value={formik.values.email}
        onChange={formik.handleChange}
        error={formik.touched.email && Boolean(formik.errors.email)}
        margin="normal"
        sx={{ mr: 3 }}
        autoComplete="email"
      />

      {(error || errorApi) && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {t(
            `api_errors:${(error || errorApi) as 'invalid_username_or_password'}`,
            t('api_errors:generic_error'),
          )}
        </Alert>
      )}

      <LoadingButton
        loading={loading}
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        sx={{ mb: 2, mt: 4 }}
      >
        {t('recover_password:recover_password')}
      </LoadingButton>
      <Link to="/login" component={LinkRouter} sx={{ textDecoration: 'none' }}>
        <Button fullWidth size="large">
          {t('recover_password:back_to_login')}
        </Button>
      </Link>
    </Paper>
  );
}

export default RecoverPassword;
